import { useMemo, useState } from 'react';
import { FormState } from 'final-form';

import { OfferFragment } from '@occc/graphql';

import { useGetOfferOperationHandlers } from './useGetOfferOperationHandlers';

import { GetOperations } from '../types';

interface ExecuteOperationsProps {
  offerId: string;
  offer: OfferFragment | undefined;
  getOperations: GetOperations;
  useGetOperationsHandlers?: typeof useGetOfferOperationHandlers;
}

export const useExecuteOperations = <FormValues, InitialFormValues = Partial<FormValues>>({
  offerId,
  offer,
  getOperations,
  useGetOperationsHandlers = useGetOfferOperationHandlers,
}: ExecuteOperationsProps): [
  (formValues: FormValues, state: FormState<FormValues, InitialFormValues>) => Promise<void>,
  boolean,
] => {
  const operationHandlers = useGetOperationsHandlers();
  const [loading, setLoading] = useState(false);

  const executeOperations = useMemo(
    () => async (formValues: FormValues, state: FormState<FormValues, InitialFormValues>) => {
      const operations = getOperations(offerId, offer, formValues, state.dirtyFields);
      try {
        setLoading(true);
        for (const operation of operations) {
          const handler = operationHandlers[operation.name];
          if (!handler) {
            throw new Error('Operation ' + operation.name + ' handler is not registered');
          }

          await handler({
            variables: operation.variables,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [getOperations, offerId, offer, operationHandlers],
  );
  return [executeOperations, loading];
};
